/**
 * Shared atoms for the printed fulfilment documents (§12) — the Alpine Nation
 * brand mark (paper ink, not the dark-theme tokens), a postal address block, and
 * small formatters. Kept here so the packing slip and picking list render
 * identical branding/addresses.
 */
import type { ReactNode } from "react";
import type { DocAddress } from "../../lib/api.js";
import { countryName } from "../../lib/countries.js";

/** Alpine Nation document logomark — fixed paper colours (a trail cairn). */
export function DocBrand({ subtitle }: { subtitle: string }) {
  return (
    <div className="doc-brand">
      <svg className="doc-brand__mark" width="38" height="38" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="17.5" width="16" height="4" rx="2" fill="#3a3f45" />
        <rect x="6" y="12.5" width="12" height="4" rx="2" fill="#1a1d21" />
        <rect x="8.5" y="8" width="7" height="3.6" rx="1.8" fill="#1a1d21" />
        <rect x="9.5" y="3.4" width="5" height="3.4" rx="1.7" fill="#0b7689" />
      </svg>
      <div>
        <div className="doc-brand__name">Alpine Nation</div>
        <div className="doc-brand__sub">{subtitle}</div>
      </div>
    </div>
  );
}

/** Render a postal party (name, company, street, city, country, phone). */
export function AddressBlock({
  label,
  addr,
  fallback,
}: {
  label: string;
  addr: DocAddress | null;
  fallback?: ReactNode;
}) {
  return (
    <div className="doc-party">
      <div className="doc-party__label">{label}</div>
      {addr ? (
        <address>
          {addr.name && <span className="doc-party__line doc-party__name">{addr.name}</span>}
          {addr.company && <span className="doc-party__line">{addr.company}</span>}
          {addr.street1 && <span className="doc-party__line">{addr.street1}</span>}
          {addr.street2 && <span className="doc-party__line">{addr.street2}</span>}
          <span className="doc-party__line">
            {[addr.postcode, addr.city].filter(Boolean).join(" ")}
            {addr.province ? `, ${addr.province}` : ""}
          </span>
          {addr.country && <span className="doc-party__line">{countryName(addr.country) || addr.country}</span>}
          {addr.phone && <span className="doc-party__line doc-party__muted">{addr.phone}</span>}
          {addr.email && <span className="doc-party__line doc-party__muted">{addr.email}</span>}
        </address>
      ) : (
        <span className="doc-party__muted">{fallback ?? "—"}</span>
      )}
    </div>
  );
}

/** "445 g" for small parcels, "1.24 kg" once over 1 kg. "—" for null. */
export function fmtGrams(g: number | null | undefined): string {
  if (g == null || !Number.isFinite(g)) return "—";
  return g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g} g`;
}

/** Date + time for a document header, locale-stable enough for screenshots. */
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
